import jsPDF from 'jspdf';
import QRCode from 'qrcode';

export interface QRLabelItem {
  qrCodeValue: string;
  qrCodeText: string;
  productName: string;
  unitNumber: string;
  imageUrl?: string;
}

export interface QRLabelPdfOptions {
  title?: string;
  filename?: string;
}

// A4 dimensions in mm
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 10;
const GAP = 6;

// Grid: 2 cols x 3 rows = 6 labels per page
const COLS = 2;
const ROWS = 3;
const LABEL_W = (PAGE_W - MARGIN * 2 - GAP * (COLS - 1)) / COLS;
const LABEL_H = (PAGE_H - MARGIN * 2 - GAP * (ROWS - 1) - 14) / ROWS;

const LABEL_PAD = 5;

async function loadImageAsDataUrl(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    setTimeout(() => resolve(null), 5000);
    img.src = url;
  });
}

async function generateQrDataUrl(value: string): Promise<string> {
  return QRCode.toDataURL(value, {
    width: 256,
    margin: 1,
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light: '#ffffff' },
  });
}

function truncateText(doc: jsPDF, text: string, maxWidth: number): string {
  if (doc.getTextWidth(text) <= maxWidth) return text;
  let t = text;
  while (t.length > 0 && doc.getTextWidth(t + '...') > maxWidth) t = t.slice(0, -1);
  return t + '...';
}

function drawImagePlaceholder(doc: jsPDF, x: number, y: number, size: number) {
  doc.setDrawColor(180, 180, 180);
  doc.setFillColor(245, 245, 245);
  doc.setLineWidth(0.2);
  doc.roundedRect(x, y, size, size, 1, 1, 'FD');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(size > 50 ? 10 : 6);
  doc.setTextColor(160, 160, 160);
  const txt = 'No Image';
  const tw = doc.getTextWidth(txt);
  doc.text(txt, x + (size - tw) / 2, y + size / 2 + 1);
}

function addImage(doc: jsPDF, dataUrl: string | null, x: number, y: number, size: number) {
  if (dataUrl) {
    try {
      doc.addImage(dataUrl, 'PNG', x, y, size, size);
      return;
    } catch { /* fallthrough */ }
  }
  drawImagePlaceholder(doc, x, y, size);
}

// ─── Grid label (multi-item) ──────────────────────────────────────
// Layout:
//   Top row:   [Image 28mm]  Product Name
//                            Unit Number
//   Bottom:    [QR code centered in full width]
//              QR code text

function drawLabel(
  doc: jsPDF,
  item: QRLabelItem,
  x: number,
  y: number,
  imgDataUrl: string | null,
  qrDataUrl: string
) {
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, LABEL_W, LABEL_H, 2, 2, 'S');

  const ix = x + LABEL_PAD;
  const iy = y + LABEL_PAD;
  const cw = LABEL_W - LABEL_PAD * 2;
  const imgSize = 28;

  // ── Top: image + text ──
  addImage(doc, imgDataUrl, ix, iy, imgSize);

  const tx = ix + imgSize + 5;
  const tmw = cw - imgSize - 5;
  let ty = iy + 5;

  // Product name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 30, 30);
  const nameLines = doc.splitTextToSize(item.productName, tmw) as string[];
  nameLines.slice(0, 2).forEach((line: string) => {
    doc.text(line, tx, ty);
    ty += 4.5;
  });

  // Unit number
  ty += 2;
  doc.setFont('courier', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(50, 50, 50);
  doc.text(truncateText(doc, item.unitNumber, tmw), tx, ty);

  // ── Bottom: QR code centered ──
  const qrSize = 38;
  const qrX = x + (LABEL_W - qrSize) / 2;
  const qrY = iy + imgSize + 6;
  doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

  // QR code text centered below
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  const ctw = doc.getTextWidth(item.qrCodeText);
  doc.text(item.qrCodeText, x + (LABEL_W - ctw) / 2, qrY + qrSize + 4);
}

// ─── Full-page label (single item) ───────────────────────────────

function drawFullPageLabel(
  doc: jsPDF,
  item: QRLabelItem,
  imgDataUrl: string | null,
  qrDataUrl: string,
  title?: string
) {
  drawPageHeader(doc, title);

  const cx = PAGE_W / 2;
  const top = 35;
  const maxW = PAGE_W - MARGIN * 2 - 20;

  // Product image centered
  const imgSize = 70;
  addImage(doc, imgDataUrl, cx - imgSize / 2, top, imgSize);

  // Product name
  let ty = top + imgSize + 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(30, 30, 30);
  (doc.splitTextToSize(item.productName, maxW) as string[]).slice(0, 3).forEach((line: string) => {
    doc.text(line, cx - doc.getTextWidth(line) / 2, ty);
    ty += 8;
  });

  // Unit number
  ty += 4;
  doc.setFont('courier', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(50, 50, 50);
  const uw = doc.getTextWidth(item.unitNumber);
  doc.text(item.unitNumber, cx - uw / 2, ty);

  // QR code centered
  const qrSize = 80;
  const qrY = ty + 12;
  doc.addImage(qrDataUrl, 'PNG', cx - qrSize / 2, qrY, qrSize, qrSize);

  // QR code text
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(120, 120, 120);
  const cw = doc.getTextWidth(item.qrCodeText);
  doc.text(item.qrCodeText, cx - cw / 2, qrY + qrSize + 7);
}

// ─── Page header ──────────────────────────────────────────────────

function drawPageHeader(doc: jsPDF, title?: string) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(48, 76, 90);
  doc.text('homebound', MARGIN, MARGIN + 6);

  const dateStr = new Date().toLocaleDateString('en-GB');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);

  if (title) {
    doc.text(title, PAGE_W - MARGIN, MARGIN + 3, { align: 'right' });
    doc.text(dateStr, PAGE_W - MARGIN, MARGIN + 7, { align: 'right' });
  } else {
    doc.text(dateStr, PAGE_W - MARGIN, MARGIN + 6, { align: 'right' });
  }
}

// ─── Main entry point ─────────────────────────────────────────────

export async function generateQRLabelPdf(
  items: QRLabelItem[],
  options?: QRLabelPdfOptions
): Promise<void> {
  if (items.length === 0) return;

  const [imageResults, qrResults] = await Promise.all([
    Promise.allSettled(items.map(i => i.imageUrl ? loadImageAsDataUrl(i.imageUrl) : Promise.resolve(null))),
    Promise.allSettled(items.map(i => generateQrDataUrl(i.qrCodeValue))),
  ]);

  const images = imageResults.map(r => r.status === 'fulfilled' ? r.value : null);
  const qrCodes = qrResults.map(r => r.status === 'fulfilled' ? r.value : '');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  if (items.length === 1) {
    drawFullPageLabel(doc, items[0], images[0], qrCodes[0], options?.title);
  } else {
    const perPage = COLS * ROWS;
    const headerH = 14;

    items.forEach((item, i) => {
      const pos = i % perPage;
      if (pos === 0 && i > 0) doc.addPage();
      if (pos === 0) drawPageHeader(doc, options?.title);

      const col = pos % COLS;
      const row = Math.floor(pos / COLS);
      drawLabel(doc, item, MARGIN + col * (LABEL_W + GAP), MARGIN + headerH + row * (LABEL_H + GAP), images[i], qrCodes[i]);
    });
  }

  doc.save(options?.filename || 'qr-labels.pdf');
}
